import React from "react";
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Panel, ComponentLoader } from "../../components";
import TinyTable from "../../components/Table";
import { Plus, TrashEmpty, Lock, Block, CircleCheck } from "@tiny/icons";
import { useAudienceMembershipPanel } from "./AudienceMembershipPanel.hooks";
import { AudienceMembershipPanelProps } from "./AudienceMembershipPanel.types";
import {
  SEGMENTS_PAGE_SIZE,
  segmentHeaderCells,
  suppressionHeaderCells,
  SegmentTableRow,
} from "./AudienceMembershipPanel.helpers";

function AudienceMembershipPanel({ email }: AudienceMembershipPanelProps) {
  const {
    segmentRows,
    pagedSegmentRows,
    segmentsPage,
    segmentsPageCount,
    setSegmentsPage,
    suppressionRows,
    suppressed,
    customerId,
    isLoading,
    requestRemoveSegment,
    openAddSegmentDialog,
  } = useAudienceMembershipPanel({ email });

  const segmentsRangeStart = segmentsPage * SEGMENTS_PAGE_SIZE + 1;
  const segmentsRangeEnd = Math.min(
    (segmentsPage + 1) * SEGMENTS_PAGE_SIZE,
    segmentRows.length
  );

  // Compact, design-matched table density (TinyTable defaults are too roomy
  // for this membership list). Also make every header cell share one grey bar.
  const compactTableSx = {
    "& .MuiTableCell-root": {
      py: 3,
      px: 4,
    },
    "& .MuiTableCell-head": {
      backgroundColor: "grey.20",
      py: 3,
    },
  };

  const SegmentActionCTA = React.useCallback(
    ({ tableRowData }: { tableRowData: SegmentTableRow }) => (
      <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
        {tableRowData.isRemovable ? (
          <Tooltip title="Delete from audience">
            <IconButton
              size="small"
              onClick={() =>
                requestRemoveSegment({
                  segmentId: tableRowData.segmentId,
                  segmentName: tableRowData.rawName,
                })
              }
            >
              <TrashEmpty fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Managed by an integration — can't be removed here">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                p: 1,
              }}
            >
              <Lock fontSize="small" sx={{ color: "grey.60" }} />
            </Box>
          </Tooltip>
        )}
      </Box>
    ),
    [requestRemoveSegment]
  );

  if (isLoading) {
    return <ComponentLoader height="200px" />;
  }

  return (
    <Panel
      title="Audience Membership"
      subtitle="Audiences and suppression lists this contact belongs to."
      actions={
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Plus />}
          onClick={openAddSegmentDialog}
          disabled={!customerId}
        >
          Add to audience
        </Button>
      }
    >
      <Box sx={{ px: 6, pb: 6, pt: 5 }}>
        {suppressed && suppressionRows.length > 0 && (
          <Box
            sx={(theme) => ({
              backgroundColor: alpha(theme.palette.error.main, 0.08),
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
              borderRadius: 2,
              px: 4,
              py: 3,
              mb: 6,
            })}
          >
            <Block
              sx={{
                color: "error.main",
                fontSize: 18,
                verticalAlign: "middle",
                mr: 2,
              }}
            />
            <Typography
              component="span"
              variant="label2R"
              color="error.dark"
              sx={{ verticalAlign: "middle" }}
            >
              <Box component="span" sx={{ fontWeight: 700 }}>
                This contact is on {suppressionRows.length} suppression list
                {suppressionRows.length === 1 ? "" : "s"}.
              </Box>{" "}
              Campaigns won&apos;t be delivered while it stays suppressed. See
              the suppression lists below.
            </Typography>
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <Typography variant="label2" color="grey.90">
            Audiences
          </Typography>
          <Typography variant="label2R" color="grey.60">
            · {segmentRows.length}
          </Typography>
        </Box>
        {segmentRows.length === 0 ? (
          <Typography variant="label2R" color="grey.60">
            This contact isn&apos;t in any audience.{" "}
            <Button
              variant="text"
              size="small"
              onClick={openAddSegmentDialog}
              sx={{ p: 0, minWidth: 0, verticalAlign: "baseline" }}
            >
              Add to an audience
            </Button>
            .
          </Typography>
        ) : (
          <>
            <Box
              sx={{
                border: "1px solid",
                borderColor: "grey.40",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <TinyTable
                headerCells={segmentHeaderCells}
                rows={pagedSegmentRows}
                rowsData={pagedSegmentRows}
                sx={compactTableSx}
                options={{
                  currentSort: "",
                  onSort: () => undefined,
                  showActionAlways: true,
                  ActionCTA: SegmentActionCTA,
                  ActionColCTA: (
                    <Typography variant="label2R" color="grey.80">
                      Actions
                    </Typography>
                  ),
                }}
              />
              {segmentRows.length > SEGMENTS_PAGE_SIZE && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 4,
                    py: 3,
                    borderTop: "1px solid",
                    borderColor: "grey.40",
                  }}
                >
                  <Typography variant="label2R" color="grey.80">
                    {segmentsRangeStart}-{segmentsRangeEnd} of{" "}
                    {segmentRows.length}
                  </Typography>
                  <Pagination
                    count={segmentsPageCount}
                    page={segmentsPage + 1}
                    onChange={(_event, newPage) => setSegmentsPage(newPage - 1)}
                    shape="rounded"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          </>
        )}

        <Box display="flex" alignItems="center" gap={1} mt={8} mb={3}>
          <Typography variant="label2" color="grey.90">
            Suppressions
          </Typography>
          <Typography variant="label2R" color="grey.60">
            · {suppressionRows.length}
          </Typography>
        </Box>
        {suppressionRows.length === 0 ? (
          <Box display="flex" alignItems="center" gap={2}>
            <CircleCheck color="success" fontSize="small" />
            <Typography variant="label2R" color="grey.60">
              Not on any suppression list — this contact can receive campaigns.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              border: "1px solid",
              borderColor: "grey.40",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <TinyTable
              headerCells={suppressionHeaderCells}
              rows={suppressionRows}
              rowsData={suppressionRows}
              sx={compactTableSx}
              options={{
                currentSort: "",
                onSort: () => undefined,
                showActionAlways: false,
              }}
            />
          </Box>
        )}
      </Box>
    </Panel>
  );
}

export default AudienceMembershipPanel;
